const SM_API_BASE_URL = import.meta.env.VITE_SM_API_URL || "";

export class SmApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "SmApiError";
    this.status = status;
  }
}

export type SmProfile = Record<string, unknown>;

export interface PoiContentItem {
  title: string;
  url: string;
  source: string;
  type: string;
}

export interface PoiLocalizedText {
  fi?: string;
  sv?: string;
  en?: string;
}

export interface PoiProperties {
  port_number?: number;
  harbourchart_url?: string;
  scout_harbour_chart_url?: string;
  scout_aerial_image_url?: string;
  mooring?: string;
  depth?: string;
  places?: string;
  description?: PoiLocalizedText;
  scout_description?: {
    fi?: string;
    sv?: string;
  };
  mk_description?: {
    fi?: string;
    sv?: string;
  };
  mk_harbour_image_urls?: Array<string | null | undefined>;
  services?: string[];
  septit_net_url?: {
    en?: string;
    fi?: string;
    sv?: string;
  };
}

export interface TotalveneArticle {
  article_id: string;
  url: string;
  publish_date: string;
  title: string;
  image_thumbnail: string;
  short_title: string;
}

export interface PoiItem {
  id: string | number;
  is_official: boolean;
  is_scout_port: boolean;
  is_spv?: boolean;
  /** Present (true/false) marks Merikarhut ports; null/absent = regular harbor. */
  is_mk_port?: boolean | null;
  lat: number;
  lon: number;
  name: string;
  poi_type: string;
  contents?: PoiContentItem[];
  properties?: PoiProperties;
  totalvene_articles?: TotalveneArticle[];
  point_of_interest_properties?: Array<{
    key: string;
    value: string | number | boolean | null;
  }>;
}

export interface SmRouteWaypoint {
  order_id?: number;
  fairway: boolean;
  latitude: number;
  longitude: number;
  userpoint?: boolean;
  tracked?: boolean;
}

export interface SmRoute {
  id?: number;
  name: string;
  user_id?: number;
  created_at?: string;
  updated_at?: string;
  length: number;
  authorization_hash?: string;
  waypoint_count?: number;
  waypoints_count?: number;
  /** When false, the client must not move or remove waypoints on this route. */
  editable?: boolean;
  waypoints: SmRouteWaypoint[];
}

export type SmRoutePayload = Pick<SmRoute, "name" | "length" | "waypoints">;

export type SmSavedPlaceCategory =
  | "FAVORITE"
  | "FISH"
  | "COTTAGE"
  | "WARNING"
  | "INFO"
  | "SIGHT"
  | "ANCHOR";

export interface SmPoiImageVariants {
  thumbnail: string;
  /** JSON key is `public` */
  public: string;
}

export interface SmPoiImage {
  id: number;
  position: number;
  variants: SmPoiImageVariants;
}

export interface SmUserPointOfInterest {
  id?: number | string;
  name: string;
  lat: number;
  lon: number;
  poi_type?: string;
  is_scout_port?: boolean;
  is_official?: boolean;
  is_spv?: boolean;
  description?: string | null;
  category?: SmSavedPlaceCategory | string | null;
  images?: SmPoiImage[];
  created_at?: string;
  updated_at?: string;
}

export type SmUserPointOfInterestPayload = {
  name: string;
  lat: number;
  lon: number;
  description?: string | null;
  category?: SmSavedPlaceCategory | string | null;
  poi_type?: string;
  is_scout_port?: boolean;
  is_official?: boolean;
  is_spv?: boolean;
};

export interface SmPoiImageUploadUrl {
  upload_url: string;
  image_id: string;
}

function getSmApiBaseUrl(): string {
  if (!SM_API_BASE_URL) {
    throw new Error("Missing VITE_SM_API_URL");
  }
  return SM_API_BASE_URL;
}

async function requestSmApi<T>(
  path: string,
  accessToken: string,
  options?: RequestInit,
): Promise<T> {
  const url = new URL(path, getSmApiBaseUrl());
  const response = await fetch(url.toString(), {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(options?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    console.error("SM API request failed", {
      path,
      status: response.status,
      statusText: response.statusText,
      body: errorText,
    });
    throw new SmApiError(
      `SM API request failed: ${response.status}`,
      response.status,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/** Single rating entry (GET response and POST body item). */
export interface SmPoiRating {
  /** Overall harbour vote is `"general"` — not `port.rating.category.first`. */
  type: string;
  score: number;
  comment?: string | null;
}

export interface PointOfInterestRating {
  point_of_interest_id: string;
  all_ratings: SmPoiRating[];
  user_ratings: SmPoiRating[];
}

/** Coerce one rating object from API JSON (handles `Type` vs `type`, etc.). */
function normalizeSmPoiRating(raw: unknown): SmPoiRating | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const typeVal =
    o.type ?? o.Type ?? o.rating_type ?? o.ratingType ?? o.kind;
  if (typeVal == null || typeVal === "") return null;
  const type = String(typeVal).trim();
  const scoreRaw = o.score ?? o.Score;
  const scoreNum =
    typeof scoreRaw === "number" && Number.isFinite(scoreRaw)
      ? scoreRaw
      : Number(scoreRaw);
  if (!Number.isFinite(scoreNum)) return null;
  const c = o.comment ?? o.Comment;
  const comment =
    c == null || c === "" ? null : String(c);
  return { type, score: scoreNum, comment };
}

function normalizeRatingsList(arr: unknown): SmPoiRating[] {
  if (!Array.isArray(arr)) return [];
  const out: SmPoiRating[] = [];
  for (const item of arr) {
    const r = normalizeSmPoiRating(item);
    if (r) out.push(r);
  }
  return out;
}

/** Normalize GET/POST ratings payload so `type` is always a reliable string for lookups. */
export function normalizePointOfInterestRating(
  raw: unknown,
): PointOfInterestRating {
  if (!raw || typeof raw !== "object") {
    return { point_of_interest_id: "", all_ratings: [], user_ratings: [] };
  }
  const o = raw as Record<string, unknown>;
  const pid =
    typeof o.point_of_interest_id === "string"
      ? o.point_of_interest_id
      : o.pointOfInterestId != null
        ? String(o.pointOfInterestId)
        : "";
  return {
    point_of_interest_id: pid,
    all_ratings: normalizeRatingsList(o.all_ratings),
    user_ratings: normalizeRatingsList(o.user_ratings),
  };
}

export async function fetchPointOfInterestRatings(
  accessToken: string,
  pointOfInterestId: string,
): Promise<PointOfInterestRating> {
  const path = `/point_of_interests/${encodeURIComponent(pointOfInterestId)}/ratings`;
  const data = await requestSmApi<PointOfInterestRating>(path, accessToken, {
    method: "GET",
  });
  if (import.meta.env.DEV) {
    console.log(`[smApi] GET ${path}`, data);
  }
  return normalizePointOfInterestRating(data as unknown);
}

export async function savePointOfInterestRating(
  accessToken: string,
  pointOfInterestId: string,
  ratings: SmPoiRating[],
): Promise<PointOfInterestRating> {
  const data = await requestSmApi<PointOfInterestRating>(
    `/point_of_interests/${encodeURIComponent(pointOfInterestId)}/ratings`,
    accessToken,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(ratings),
    },
  );
  return normalizePointOfInterestRating(data as unknown);
}

/** `GET /profile` — use Cognito ID token as Bearer (falls back to access token at call sites if needed). */
export async function fetchProfile(idToken: string): Promise<SmProfile> {
  if (!SM_API_BASE_URL) {
    throw new Error("Missing VITE_SM_API_URL");
  }

  const url = new URL("/profile", SM_API_BASE_URL);
  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    console.error("Profile request failed", {
      status: response.status,
      statusText: response.statusText,
      body: errorText,
    });
    throw new SmApiError(
      `Profile request failed: ${response.status}`,
      response.status,
    );
  }

  const profile = (await response.json()) as SmProfile;
  if (import.meta.env.DEV) {
    console.log("[smApi] GET /profile", profile);
  }
  return profile;
}

export interface SmUserPayload {
  email: string;
  password: string;
  password_confirmation: string;
  first_name: string;
  last_name: string;
}

export interface SmSignupResponse extends Record<string, unknown> {
  token?: string;
}

function generateRandomPassword(length = 16): string {
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);
  return Array.from(values, (value) => alphabet[value % alphabet.length]).join(
    "",
  );
}

export async function createSmUser(
  payload: Omit<SmUserPayload, "password" | "password_confirmation"> & {
    password?: string;
  },
): Promise<SmSignupResponse> {
  if (!SM_API_BASE_URL) {
    throw new Error("Missing VITE_SM_API_URL");
  }

  const password = payload.password ?? generateRandomPassword(16);
  const url = new URL("/users.json", SM_API_BASE_URL);
  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      user: {
        email: payload.email,
        password,
        password_confirmation: password,
        first_name: payload.first_name,
        last_name: payload.last_name,
      },
    }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    console.error("Signup request failed", {
      status: response.status,
      statusText: response.statusText,
      body: errorText,
    });
    throw new SmApiError(
      `Signup request failed: ${response.status}`,
      response.status,
    );
  }

  return (await response.json()) as SmSignupResponse;
}

export async function linkSmUser(
  authToken: string,
  apiKey: string,
): Promise<void> {
  if (!SM_API_BASE_URL) {
    throw new Error("Missing VITE_SM_API_URL");
  }

  const url = new URL("/link_user", SM_API_BASE_URL);
  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${authToken}`,
      "x-api-key": apiKey,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    console.error("Link request failed", {
      status: response.status,
      statusText: response.statusText,
      body: errorText,
    });
    throw new SmApiError(
      `Link request failed: ${response.status}`,
      response.status,
    );
  }
}

export async function deleteUser(
  accessToken: string,
  userId: number,
): Promise<void> {
  await requestSmApi<void>(`/users/${userId}`, accessToken, {
    method: "DELETE",
  });
}

export type PoiFilter =
  | "ports"
  | "ports_and_scout_ports"
  | "ports_and_scout_and_mk_ports";

export async function fetchPointOfInterests(
  accessToken?: string,
  filter: PoiFilter = "ports",
): Promise<PoiItem[]> {
  if (!SM_API_BASE_URL) {
    throw new Error("Missing VITE_SM_API_URL");
  }

  const url = new URL("/v3/point_of_interests", SM_API_BASE_URL);
  url.searchParams.set("filter", filter);

  const headers: HeadersInit = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(url.toString(), {
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    console.error("Point of interests request failed", {
      status: response.status,
      statusText: response.statusText,
      body: errorText,
    });
    throw new Error(`Point of interests request failed: ${response.status}`);
  }

  const json = (await response.json()) as unknown;
  if (!Array.isArray(json)) {
    return [];
  }
  return json as PoiItem[];
}

export type NavWarningGeometryKind = "point" | "line" | "area";

export interface NavWarningGeoJSONFeature {
  type: "Feature";
  id?: string | number;
  geometry: {
    type: string;
    coordinates: unknown;
  };
  properties: Record<string, unknown>;
}

export interface NavWarningFeatureCollection {
  type: "FeatureCollection";
  features: NavWarningGeoJSONFeature[];
}

/**
 * Fetches current navigational warnings (points, lines, areas) — sailmate_api
 * fetches and merges Traficom's WFS layers (Merivaroitukset open data)
 * server-side and caches the result for an hour.
 */
export async function fetchNavigationalWarnings(): Promise<NavWarningFeatureCollection> {
  try {
    if (!SM_API_BASE_URL) {
      throw new Error("Missing VITE_SM_API_URL");
    }

    const url = new URL("/navigational_warnings", SM_API_BASE_URL);
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(
        `Navigational warnings request failed: HTTP ${response.status}`,
      );
    }

    const data = (await response.json()) as Partial<NavWarningFeatureCollection>;
    if (!data || !Array.isArray(data.features)) {
      return { type: "FeatureCollection", features: [] };
    }

    return { type: "FeatureCollection", features: data.features };
  } catch (error) {
    console.error("Failed to fetch navigational warnings", error);
    return { type: "FeatureCollection", features: [] };
  }
}

export async function fetchUserRoutes(
  accessToken: string,
  userId: number,
): Promise<SmRoute[]> {
  const routes = await requestSmApi<SmRoute[]>(
    `/users/${userId}/routes`,
    accessToken,
  );

  // Backend may return ids as strings; normalize to number to keep UI comparisons consistent.
  return routes.map((r) => {
    const rawId = (r as unknown as { id?: unknown }).id;
    const n = Number(rawId);
    return {
      ...r,
      id: rawId == null ? undefined : Number.isFinite(n) ? n : undefined,
    };
  });
}

export async function fetchUserRoute(
  accessToken: string,
  userId: number,
  routeId: number,
): Promise<SmRoute> {
  const route = await requestSmApi<SmRoute>(
    `/users/${userId}/routes/${routeId}`,
    accessToken,
  );
  const rawId = (route as unknown as { id?: unknown }).id;
  const n = Number(rawId);
  return {
    ...route,
    id: rawId == null ? undefined : Number.isFinite(n) ? n : undefined,
  };
}

export async function createUserRoute(
  accessToken: string,
  userId: number,
  route: SmRoutePayload,
): Promise<SmRoute> {
  const created = await requestSmApi<SmRoute>(`/users/${userId}/routes`, accessToken, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(route),
  });
  const rawId = (created as unknown as { id?: unknown }).id;
  const n = Number(rawId);
  return {
    ...created,
    id: rawId == null ? undefined : Number.isFinite(n) ? n : undefined,
  };
}

export async function updateUserRoute(
  accessToken: string,
  userId: number,
  routeId: number,
  route: SmRoutePayload,
): Promise<SmRoute> {
  //console.log("updateUserRoute", userId, routeId, route);
  // return false;
  const updated = await requestSmApi<SmRoute>(
    `/users/${userId}/routes/${routeId}`,
    accessToken,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(route),
    },
  );
  const rawId = (updated as unknown as { id?: unknown }).id;
  const n = Number(rawId);
  return {
    ...updated,
    id: rawId == null ? undefined : Number.isFinite(n) ? n : undefined,
  };
}

export async function deleteUserRoute(
  accessToken: string,
  userId: number,
  routeId: number,
): Promise<void> {
  await requestSmApi<void>(`/users/${userId}/routes/${routeId}`, accessToken, {
    method: "DELETE",
  });
}

function normalizeUserPoi(raw: unknown): SmUserPointOfInterest {
  if (!raw || typeof raw !== "object") {
    throw new SmApiError("Invalid point of interest response", 500);
  }
  const o = raw as Record<string, unknown>;
  // Legacy wrapper support
  if (o.point_of_interest && typeof o.point_of_interest === "object") {
    return normalizeUserPoi(o.point_of_interest);
  }
  const lat = Number(o.lat);
  const lon = Number(o.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    throw new SmApiError("Invalid point of interest coordinates", 500);
  }
  const imagesRaw = o.images;
  const images: SmPoiImage[] | undefined = Array.isArray(imagesRaw)
    ? imagesRaw
        .map((item): SmPoiImage | null => {
          if (!item || typeof item !== "object") return null;
          const img = item as Record<string, unknown>;
          const id = Number(img.id);
          const position = Number(img.position);
          const variantsRaw = img.variants;
          if (
            !Number.isFinite(id) ||
            !Number.isFinite(position) ||
            !variantsRaw ||
            typeof variantsRaw !== "object"
          ) {
            return null;
          }
          const v = variantsRaw as Record<string, unknown>;
          const thumbnail = typeof v.thumbnail === "string" ? v.thumbnail : "";
          const publicUrl =
            typeof v.public === "string"
              ? v.public
              : typeof v.publicUrl === "string"
                ? v.publicUrl
                : "";
          if (!thumbnail && !publicUrl) return null;
          return {
            id,
            position,
            variants: { thumbnail, public: publicUrl },
          };
        })
        .filter((x): x is SmPoiImage => x !== null)
    : undefined;

  return {
    id: o.id as number | string | undefined,
    name: typeof o.name === "string" ? o.name : "",
    lat,
    lon,
    poi_type: typeof o.poi_type === "string" ? o.poi_type : undefined,
    is_scout_port:
      typeof o.is_scout_port === "boolean" ? o.is_scout_port : undefined,
    is_official: typeof o.is_official === "boolean" ? o.is_official : undefined,
    is_spv: typeof o.is_spv === "boolean" ? o.is_spv : undefined,
    description:
      typeof o.description === "string"
        ? o.description
        : o.description === null
          ? null
          : undefined,
    category:
      typeof o.category === "string"
        ? o.category
        : o.category === null
          ? null
          : undefined,
    images,
    created_at: typeof o.created_at === "string" ? o.created_at : undefined,
    updated_at: typeof o.updated_at === "string" ? o.updated_at : undefined,
  };
}

function buildPrivatePoiBody(
  payload: SmUserPointOfInterestPayload,
): Record<string, unknown> {
  return {
    name: payload.name,
    lat: payload.lat,
    lon: payload.lon,
    poi_type: payload.poi_type ?? "PRIVATE",
    is_scout_port: payload.is_scout_port ?? false,
    is_official: payload.is_official ?? false,
    is_spv: payload.is_spv ?? false,
    description: payload.description ?? null,
    category: payload.category ?? "FAVORITE",
  };
}

export async function fetchUserPointOfInterests(
  accessToken: string,
  userId: number,
): Promise<SmUserPointOfInterest[]> {
  const response = await requestSmApi<unknown>(
    `/v3/users/${userId}/point_of_interests`,
    accessToken,
  );
  if (!Array.isArray(response)) {
    return [];
  }
  return response.map((item) => normalizeUserPoi(item));
}

export async function createUserPointOfInterest(
  accessToken: string,
  userId: number,
  payload: SmUserPointOfInterestPayload,
): Promise<SmUserPointOfInterest> {
  const response = await requestSmApi<unknown>(
    `/v3/users/${userId}/point_of_interests`,
    accessToken,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(buildPrivatePoiBody(payload)),
    },
  );
  return normalizeUserPoi(response);
}

export async function updateUserPointOfInterest(
  accessToken: string,
  userId: number,
  poiId: number,
  payload: SmUserPointOfInterestPayload,
): Promise<SmUserPointOfInterest> {
  const response = await requestSmApi<unknown>(
    `/v3/users/${userId}/point_of_interests/${poiId}`,
    accessToken,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...buildPrivatePoiBody(payload),
        id: poiId,
      }),
    },
  );
  return normalizeUserPoi(response);
}

export async function deleteUserPointOfInterest(
  accessToken: string,
  userId: number,
  poiId: number,
): Promise<void> {
  await requestSmApi<void>(
    `/v3/users/${userId}/point_of_interests/${poiId}`,
    accessToken,
    {
      method: "DELETE",
    },
  );
}

export async function getUserPoiImageUploadUrl(
  accessToken: string,
  userId: number,
  poiId: number,
): Promise<SmPoiImageUploadUrl> {
  const response = await requestSmApi<SmPoiImageUploadUrl>(
    `/v3/users/${userId}/point_of_interests/${poiId}/images/upload_url`,
    accessToken,
    { method: "POST" },
  );
  if (!response?.upload_url || !response?.image_id) {
    throw new SmApiError("Invalid image upload URL response", 500);
  }
  return response;
}

function normalizePoiImage(raw: unknown): SmPoiImage {
  if (!raw || typeof raw !== "object") {
    throw new SmApiError("Invalid image response", 500);
  }
  const img = raw as Record<string, unknown>;
  const id = Number(img.id);
  const position = Number(img.position ?? 0);
  const variantsRaw = img.variants;
  if (!Number.isFinite(id) || !variantsRaw || typeof variantsRaw !== "object") {
    throw new SmApiError("Invalid image response", 500);
  }
  const v = variantsRaw as Record<string, unknown>;
  return {
    id,
    position: Number.isFinite(position) ? position : 0,
    variants: {
      thumbnail: typeof v.thumbnail === "string" ? v.thumbnail : "",
      public:
        typeof v.public === "string"
          ? v.public
          : typeof v.publicUrl === "string"
            ? v.publicUrl
            : "",
    },
  };
}

export async function confirmUserPoiImageUpload(
  accessToken: string,
  userId: number,
  poiId: number,
  cloudflareImageId: string,
): Promise<SmPoiImage> {
  const response = await requestSmApi<unknown>(
    `/v3/users/${userId}/point_of_interests/${poiId}/images`,
    accessToken,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ cloudflare_image_id: cloudflareImageId }),
    },
  );
  return normalizePoiImage(response);
}

export async function deleteUserPoiImage(
  accessToken: string,
  userId: number,
  poiId: number,
  imageId: number,
): Promise<void> {
  await requestSmApi<void>(
    `/v3/users/${userId}/point_of_interests/${poiId}/images/${imageId}`,
    accessToken,
    { method: "DELETE" },
  );
}

/** Upload a file to Cloudflare via the direct-upload URL, then confirm with API. */
export async function uploadUserPoiImage(
  accessToken: string,
  userId: number,
  poiId: number,
  file: File,
): Promise<SmPoiImage> {
  const { upload_url, image_id } = await getUserPoiImageUploadUrl(
    accessToken,
    userId,
    poiId,
  );

  const formData = new FormData();
  formData.append("file", file, file.name || "photo.jpg");

  const uploadResponse = await fetch(upload_url, {
    method: "POST",
    body: formData,
  });
  if (!uploadResponse.ok && uploadResponse.status !== 409) {
    throw new SmApiError(
      `Cloudflare image upload failed: ${uploadResponse.status}`,
      uploadResponse.status,
    );
  }

  return confirmUserPoiImageUpload(accessToken, userId, poiId, image_id);
}

interface ScoutRouteWaypoint {
  latitude: number;
  longitude: number;
  userpoint: boolean;
  fairway: boolean;
  order_id: number;
  tracked: boolean;
}

interface ScoutRoute {
  id: number;
  name: string;
  length: number;
  created_at: string;
  updated_at: string;
  waypoints: ScoutRouteWaypoint[];
}

function scoutRouteToFeature(
  route: ScoutRoute,
): GeoJSON.Feature<GeoJSON.LineString> | null {
  const coordinates: [number, number][] = (route.waypoints ?? [])
    .slice()
    .sort((a, b) => a.order_id - b.order_id)
    .map((wp) => [wp.longitude, wp.latitude] as [number, number])
    .filter(
      ([lng, lat]) =>
        Number.isFinite(lng) &&
        Number.isFinite(lat) &&
        Math.abs(lat) <= 90 &&
        Math.abs(lng) <= 180,
    );

  if (coordinates.length < 2) return null;

  return {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates,
    },
    properties: {
      id: route.id,
      name: route.name,
      length: route.length,
      created_at: route.created_at,
      updated_at: route.updated_at,
      waypoint_count: route.waypoints?.length ?? 0,
    },
  };
}

function normalizeScoutRoutesPayload(
  payload: unknown,
): GeoJSON.FeatureCollection {
  // Already a FeatureCollection
  if (
    typeof payload === "object" &&
    payload !== null &&
    "type" in payload &&
    (payload as { type?: unknown }).type === "FeatureCollection" &&
    "features" in payload &&
    Array.isArray((payload as { features?: unknown }).features)
  ) {
    const features = (payload as GeoJSON.FeatureCollection).features.flatMap(
      (feature) => {
        const geometry = feature?.geometry;
        if (!geometry) return [];
        if (
          geometry.type === "LineString" &&
          Array.isArray(geometry.coordinates) &&
          geometry.coordinates.length >= 2
        ) {
          return [feature];
        }
        if (
          geometry.type === "MultiLineString" &&
          Array.isArray(geometry.coordinates)
        ) {
          return geometry.coordinates
            .filter((line) => Array.isArray(line) && line.length >= 2)
            .map((coordinates) => ({
              type: "Feature" as const,
              geometry: { type: "LineString" as const, coordinates },
              properties: feature.properties ?? {},
            }));
        }
        return [];
      },
    );
    return { type: "FeatureCollection", features };
  }

  // Array of route records
  let routes: unknown = payload;
  if (typeof payload === "object" && payload !== null && !Array.isArray(payload)) {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.routes)) routes = obj.routes;
    else if (Array.isArray(obj.data)) routes = obj.data;
    else if (Array.isArray(obj.items)) routes = obj.items;
  }

  if (!Array.isArray(routes)) {
    console.warn("Scout routes response is not an array", payload);
    return { type: "FeatureCollection", features: [] };
  }

  const features = routes
    .map((route) => scoutRouteToFeature(route as ScoutRoute))
    .filter((feature): feature is GeoJSON.Feature<GeoJSON.LineString> =>
      Boolean(feature),
    );

  return { type: "FeatureCollection", features };
}

export async function fetchScoutRoutes(
  accessToken: string,
): Promise<GeoJSON.FeatureCollection> {
  const url = new URL("/routes", getSmApiBaseUrl());
  url.searchParams.set("scout_routes", "true");

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    console.error("Scout routes request failed", {
      status: response.status,
      statusText: response.statusText,
      body: errorText,
    });
    throw new SmApiError(
      `Scout routes request failed: ${response.status}`,
      response.status,
    );
  }

  const payload = (await response.json()) as unknown;
  return normalizeScoutRoutesPayload(payload);
}

export type SmSubscriptionStore =
  | "app_store"
  | "play_store"
  | "stripe"
  | "promotional"
  | (string & {});

export interface SmSubscriptionEntitlement {
  expires_date: string | null;
  grace_period_expires_date: string | null;
  product_identifier: string;
  product_plan_identifier?: string;
  purchase_date: string;
}

export interface SmSubscriptionInfo {
  auto_resume_date: string | null;
  billing_issues_detected_at: string | null;
  display_name: string | null;
  expires_date: string | null;
  grace_period_expires_date: string | null;
  is_sandbox: boolean;
  management_url?: string | null;
  original_purchase_date: string | null;
  period_type: string | null;
  price?: {
    amount: number;
    currency: string;
  } | null;
  product_plan_identifier?: string;
  purchase_date: string;
  refunded_at: string | null;
  store: SmSubscriptionStore;
  store_transaction_id: string | null;
  unsubscribe_detected_at: string | null;
}

export interface SmUserSubscriptionsResponse {
  entitlements?: Record<string, SmSubscriptionEntitlement>;
  subscriptions?: Record<string, SmSubscriptionInfo>;
  management_url?: string | null;
  [key: string]: unknown;
}

export async function fetchUserSubscriptions(
  accessToken: string,
  userId: number,
): Promise<SmUserSubscriptionsResponse> {
  return requestSmApi<SmUserSubscriptionsResponse>(
    `/users/${userId}/subscriptions`,
    accessToken,
  );
}

const TILE_API_BASE_URL =
  "https://app-tiles-api-1074576217688.europe-north1.run.app";

export type TileApiTech = "4g" | "5g";
export type TileApiOperatorMnc = "05" | "12" | "91";

export interface TileApiTileResponse {
  tile_id?: string | number;
  id?: string | number;
  northwest_latitude: number | string;
  northwest_longitude: number | string;
  southeast_latitude: number | string;
  southeast_longitude: number | string;
  download?: number | string | null;
  upload?: number | string | null;
  latency?: number | string | null;
  rsrp?: number | string | null;
}

export interface FetchNetworkTilesByBoundsParams {
  mcc?: string;
  mnc: TileApiOperatorMnc;
  tech: TileApiTech;
  zoom: number;
  northwest: {
    lat: number;
    lng: number;
  };
  southeast: {
    lat: number;
    lng: number;
  };
  signal?: AbortSignal;
}

function toTileApiNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export async function fetchNetworkTilesByBounds({
  mcc = "244",
  mnc,
  tech,
  zoom,
  northwest,
  southeast,
  signal,
}: FetchNetworkTilesByBoundsParams): Promise<TileApiTileResponse[]> {
  const safeZoom = Math.max(0, Math.round(zoom));
  const url =
    `${TILE_API_BASE_URL}/api/appTiles/getTiles/` +
    `${encodeURIComponent(mcc)}/${encodeURIComponent(mnc)}/` +
    `${encodeURIComponent(String(northwest.lat))}/${encodeURIComponent(String(northwest.lng))}/` +
    `${encodeURIComponent(String(southeast.lat))}/${encodeURIComponent(String(southeast.lng))}/` +
    `${encodeURIComponent(String(safeZoom))}/${encodeURIComponent(tech)}`;

  if (import.meta.env.PROD !== true) {
    console.log("[networkTiles] Fetch URL:", url);
  }

  const response = await fetch(url, { method: "GET", signal });
  if (!response.ok) {
    throw new Error(`Tile API request failed: ${response.status}`);
  }

  const json = (await response.json()) as unknown;
  if (!Array.isArray(json)) return [];

  return json
    .map((item): TileApiTileResponse | null => {
      if (!item || typeof item !== "object") return null;
      const obj = item as Record<string, unknown>;
      const nwLat = toTileApiNumber(obj.northwest_latitude);
      const nwLng = toTileApiNumber(obj.northwest_longitude);
      const seLat = toTileApiNumber(obj.southeast_latitude);
      const seLng = toTileApiNumber(obj.southeast_longitude);
      if (nwLat === null || nwLng === null || seLat === null || seLng === null) {
        return null;
      }
      return {
        tile_id: obj.tile_id as string | number | undefined,
        id: obj.id as string | number | undefined,
        northwest_latitude: nwLat,
        northwest_longitude: nwLng,
        southeast_latitude: seLat,
        southeast_longitude: seLng,
        download: toTileApiNumber(obj.download),
        upload: toTileApiNumber(obj.upload),
        latency: toTileApiNumber(obj.latency),
        rsrp: toTileApiNumber(obj.rsrp),
      };
    })
    .filter((tile): tile is TileApiTileResponse => tile !== null);
}
